import { useEffect, useState } from "react";
import Page from "../../components/layout/Page";
import { useFetch } from "../../helpers/useFetch";
import useTitle from "../../hooks/useTitle";
import { Card, Grid, Group, Loader, Text } from "@mantine/core";
import { IconLockOpen } from "@tabler/icons-react";
import { toast } from "react-hot-toast";
import { openGameAccountModal } from "../../helpers/modals";
import { openConfirmModal } from "@mantine/modals";
import { modalTitle } from "../../components/modals/Modal";
import { usePermissions } from "../../providers/PermissionsProvider";

const ALIAS_ICONS = {
  steam: "🎮",
  epic: "🎮",
  riot: "⚔️",
  minecraft: "⛏️",
  battlenet: "🎮",
  ubisoft: "🎮",
  ea: "🎮",
  gog: "🎮",
};

export default function GameAccountsPage() {
  const [accountTypes, setAccountTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchingType, setFetchingType] = useState(null);
  const { hasPermission } = usePermissions();

  useTitle("Herní účty");

  useEffect(() => {
    useFetch("/gamingaccounts/types").then((res) => {
      if (res?.data) setAccountTypes(res.data);
      setLoading(false);
    });
  }, []);

  const handleTypeClick = async (type) => {
    if (fetchingType !== null) return;
    setFetchingType(type.id);
    const res = await useFetch(`/gamingaccounts/free/${type.id}`);
    setFetchingType(null);

    if (res?.error) {
      toast.error(res.error);
    } else if (res?.data) {
      openGameAccountModal(res.data, type.name);
    } else {
      toast.error("Nepodařilo se získat herní účet.");
    }
  };

  const releaseAll = () => {
    openConfirmModal({
      title: modalTitle(IconLockOpen, "Uvolnit všechny účty"),
      children: (
        <Text size="sm">
          Opravdu chceš uvolnit všechny zabrané herní účty v areálu? Tato akce je nevratná a týká se všech typů účtů.
        </Text>
      ),
      labels: { confirm: "Uvolnit vše", cancel: "Zrušit" },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        const res = await useFetch("/gamingaccounts/release", "POST");
        if (res?.error) {
          toast.error(res.error);
        } else {
          toast.success(`Uvolněno ${res?.released ?? 0} účtů.`);
        }
      },
    });
  };

  const actionIcons = hasPermission("pc.survey")
    ? [
        {
          name: "Uvolnit všechny účty",
          icon: IconLockOpen,
          onTrigger: releaseAll,
        },
      ]
    : [];

  return (
    <Page title="Herní účty" subtitle="Počítačový program" actionIcons={actionIcons}>
      {loading ? (
        <Loader mt="xl" />
      ) : (
        <Grid mt="md">
          {accountTypes.map((type) => (
            <Grid.Col key={type.id} span={{ base: 12, sm: 6, md: 4 }}>
              <Card
                withBorder
                radius="md"
                padding="lg"
                style={{ cursor: fetchingType !== null ? "wait" : "pointer" }}
                onClick={() => handleTypeClick(type)}>
                <Group gap="md" wrap="nowrap">
                  <Text size="2rem" style={{ lineHeight: 1 }}>
                    {ALIAS_ICONS[type.alias] ?? "🎮"}
                  </Text>
                  <div>
                    <Text fw={700} size="lg" lh={1.2}>
                      {type.name}
                    </Text>
                    <Text size="sm" c="dimmed" mt={2}>
                      {fetchingType === type.id ? "Hledám volný účet…" : "Kliknutím získáš volný účet"}
                    </Text>
                  </div>
                </Group>
              </Card>
            </Grid.Col>
          ))}
          {accountTypes.length === 0 && (
            <Grid.Col>
              <Text c="dimmed" ta="center" mt="xl">
                Žádné typy herních účtů nejsou k dispozici.
              </Text>
            </Grid.Col>
          )}
        </Grid>
      )}
    </Page>
  );
}
